import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, AlertTriangle, Clock, TrendingUp, DollarSign, Shield, Building2, Home, Layers, ShoppingCart, Building, RefreshCw } from 'lucide-react';

const ForeclosureBailout = () => {
  const propertyTypes = [
    { name: 'Single Family (Investment)', icon: Home },
    { name: '2 to 4 Units', icon: Building2 },
    { name: 'Multifamily (5+ units)', icon: Layers },
    { name: 'Office', icon: Building },
    { name: 'Retail', icon: ShoppingCart },
    { name: 'Mixed Use', icon: Building2 },
    { name: 'Church / Special Use', icon: Shield },
  ];

  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1607270134634-75fb2ac1afd4?auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/90 to-red-950/70" />
        </div>
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-red-500/15 backdrop-blur-sm border border-red-400/20 rounded-full px-4 py-1.5 mb-8">
              <AlertTriangle className="h-4 w-4 text-red-300" />
              <span className="text-xs font-semibold text-red-200 uppercase tracking-wider">Time Sensitive Program</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Foreclosure<br /><span className="text-teal-400">Bailout</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-xl">
              Emergency bridge financing to stop foreclosure on commercial and investment properties. We move fast to beat auction dates, then help you transition into permanent financing.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/apply" data-testid="foreclosure-hero-apply" className="inline-flex items-center gap-2 bg-red-500 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-red-400 transition-all shadow-lg shadow-red-500/20">
                Get Emergency Financing <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">
                Speak With an Advisor
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights Strip */}
      <section className="bg-slate-950 py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <Clock className="h-5 w-5 text-red-400" />, value: 'Fast Funding', label: 'Beat Auction Dates' },
              { icon: <DollarSign className="h-5 w-5 text-teal-400" />, value: '2 Year Term', label: 'Bridge Loan' },
              { icon: <TrendingUp className="h-5 w-5 text-blue-400" />, value: '40 Year', label: 'Amortization' },
              { icon: <RefreshCw className="h-5 w-5 text-amber-400" />, value: 'Zero Seasoning', label: 'Cash Out Refi After' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  {stat.icon}
                </div>
                <div>
                  <div className="text-sm font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{stat.value}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works: The Bailout Bridge Loan */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-red-600 mb-4 block">How It Works</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>The Foreclosure Bailout Bridge Loan</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">Our bridge loan covers the amount owed on your defaulted loan to stop the foreclosure immediately. Lower payments through a 40 year amortization give you breathing room to stabilize.</p>
          </div>

          {/* Main program card */}
          <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 md:p-10 mb-10">
            <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Program Details</h3>
            <p className="text-slate-500 leading-relaxed mb-6">
              The bailout bridge loan covers the amount currently owed on your defaulted loan. This is not a cash out refinance. You cannot take additional money beyond the payoff amount. The purpose is singular: stop the foreclosure, protect your ownership, and give you time to stabilize.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {[
                'Covers ONLY the payoff amount owed',
                'Amortized: 2 year term, 40 year amortization',
                'Lower monthly payments than your original loan',
                'Fast funding to beat auction deadlines',
                'Stops the foreclosure process immediately',
                'Commercial and investment properties only',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600">{item}</span>
                </div>
              ))}
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
              <p className="text-sm text-teal-800 leading-relaxed">
                <span className="font-bold">Why 40 year amortization?</span> Even though the loan only covers the payoff amount, the extended amortization schedule significantly reduces your monthly payment compared to what you were paying before. This provides immediate cash flow relief and breathing room to stabilize your property.
              </p>
            </div>
          </div>

          {/* Optional: Cash Out Refi */}
          <div className="max-w-3xl mx-auto bg-slate-50 border border-slate-200 rounded-2xl p-8 md:p-10">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
                <RefreshCw className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Need Additional Capital After the Bailout?</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-3">
                  If you need extra funds for rehab, improvements, or other investments after exiting foreclosure, Quidity also offers zero seasoning cash out refinance options. This means you do not need to wait 6 to 12 months to access equity in your property. This is a separate option available to borrowers who need it, not a required part of the bailout program.
                </p>
                <div className="flex flex-wrap gap-4 mt-4">
                  <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full">No Seasoning Required</span>
                  <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full">Access Equity for Rehab or Capital</span>
                  <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full">Transition to Permanent Financing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligible Property Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Eligible Property Types</h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                This program is exclusively for commercial and investment properties, including rental income properties. Primary residences are not eligible.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {propertyTypes.map((prop) => (
                  <div key={prop.name} className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <prop.icon className="h-5 w-5 text-[#115E59] flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700">{prop.name}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-800">
                  <span className="font-bold">Note:</span> Primary residences are not eligible for this program. Single family properties must be used as investment or rental properties.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-slate-950 rounded-2xl p-8 md:p-10">
                <h3 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Bailout vs. Foreclosure</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Property Ownership', bailout: 'Retained', foreclosure: 'Lost' },
                    { label: 'Equity', bailout: 'Preserved', foreclosure: 'Forfeited' },
                    { label: 'Credit Impact', bailout: 'Minimal', foreclosure: 'Severe' },
                    { label: 'Personal Guarantee', bailout: 'Resolved', foreclosure: 'May Be Pursued' },
                    { label: 'Future Financing', bailout: 'Available Immediately', foreclosure: 'Years to Recover' },
                  ].map((row) => (
                    <div key={row.label} className="grid grid-cols-3 gap-3 text-sm">
                      <span className="text-slate-400 font-medium">{row.label}</span>
                      <span className="text-teal-400 font-semibold text-center">{row.bailout}</span>
                      <span className="text-red-400 font-semibold text-center">{row.foreclosure}</span>
                    </div>
                  ))}
                  <div className="grid grid-cols-3 gap-3 text-[10px] uppercase tracking-wider text-slate-600 pt-2 border-t border-slate-800">
                    <span></span>
                    <span className="text-center">Bailout</span>
                    <span className="text-center">Foreclosure</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>What to Expect</h2>
            <p className="text-slate-500 max-w-xl mx-auto">The foreclosure bailout process is designed for speed and simplicity.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Contact Us', desc: 'Reach out immediately with your property details, payoff amount, and auction date. The sooner we know, the faster we move.', color: 'text-red-600' },
              { step: '02', title: 'Evaluation', desc: 'We assess the property value, equity position, existing liens, and payoff requirements to structure the bailout bridge.', color: 'text-amber-600' },
              { step: '03', title: 'Fast Funding', desc: 'Once approved, we fund quickly to pay off or bring current your defaulted loan and stop the foreclosure proceedings.', color: 'text-teal-600' },
              { step: '04', title: 'Stabilize and Refi', desc: 'Enjoy lower payments during the 2 year term, then transition to a zero seasoning cash out refinance when ready.', color: 'text-blue-600' },
            ].map((item) => (
              <div key={item.step} className="bg-white border border-slate-200 rounded-2xl p-8">
                <div className={`text-3xl font-bold ${item.color} mb-4`} style={{ fontFamily: 'var(--font-heading)' }}>{item.step}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn More + CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1607270134634-75fb2ac1afd4?auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/90" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-500/15 border border-red-400/20 rounded-full px-4 py-1.5 mb-6">
                <AlertTriangle className="h-4 w-4 text-red-300" />
                <span className="text-xs font-semibold text-red-200 uppercase tracking-wider">Time Is Critical</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Do Not Wait Until It Is Too Late
              </h2>
              <p className="text-base text-slate-400 mb-4 max-w-xl">
                Every day counts when facing foreclosure. Contact us today and let us help you save your property, reduce your payments, and build a path to financial recovery.
              </p>
              <p className="text-sm text-slate-500 mb-10 max-w-lg italic">
                Bailout bridge covers the payoff amount with reduced payments. Additional options available after.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/apply" data-testid="foreclosure-bottom-apply" className="inline-flex items-center gap-2 bg-red-500 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-red-400 transition-colors shadow-lg shadow-red-500/20">
                  Get Emergency Financing <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">
                  Contact Us
                </Link>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <Link to="/academy/foreclosure-bailout-commercial-properties" className="group bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-teal-400/30 transition-all max-w-sm">
                <div className="text-xs font-bold text-teal-400 uppercase tracking-wider mb-3">Academy Guide</div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Foreclosure Bailout: The Complete Guide</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">Read our comprehensive guide covering everything you need to know about foreclosure bailouts, eligible properties, the application process, and how to prepare.</p>
                <span className="text-xs font-semibold text-teal-400 group-hover:text-teal-300 flex items-center gap-1">
                  Read the Full Guide <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForeclosureBailout;
